import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, GlassCard, GradButton, Field } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";
import { WooAktarim } from "@/components/WooAktarim";
import { HammaddeStok } from "@/components/HammaddeStok";
import { AracAktarim } from "@/components/AracAktarim";
import { KolaybiCari } from "@/components/KolaybiCari";
import { TopluEkstre } from "@/components/cari/TopluEkstre";
import { KalipAktarim } from "@/components/kalip/KalipAktarim";

const TURLER = [["kolaybi", "KolayBi'den aktar"], ["urunler", "Ürünler"], ["cariler", "Cariler (açılış bakiyeli)"], ["hammadde", "Hammadde açılış stoku"], ["araclar", "Uyumlu araçlar"], ["stoklar", "Açılış stokları"], ["woo", "WooCommerce ürünleri"], ["kaliplar", "Kalıp kartları"]];
const norm = (s) => String(s).replace(/\(.*\)/g, "").toLocaleLowerCase("tr").replace(/[^a-z0-9ğüşöçı]/g, "");

const sablonIndir = (tur, alanlar) => {
  const csv = "\ufeff" + alanlar.map((a) => a.ad.replace(" *", "")).join(";") + "\n";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `sablon-${tur}.csv`;
  a.click();
};

const tahmin = (alanlar, kolonlar) => Object.fromEntries(alanlar.map((a) => {
  const ad = norm(a.ad.replace(" *", "").split("(")[0]);
  const i = kolonlar.findIndex((k) => norm(k) === ad || norm(k) === norm(a.kod) || (ad.length > 3 && norm(k).includes(ad)));
  return [a.kod, i >= 0 ? i : null];
}));

const Sonuc = ({ r }) => (
  <GlassCard className="p-5 mt-6" data-testid="aktarim-sonuc">
    <div className="text-sm"><b className="text-emerald-300" data-testid="aktarim-eklenen">{r.eklenen}</b> kayıt eklendi · <b className={r.hatali ? "text-red-300" : ""} data-testid="aktarim-hatali">{r.hatali}</b> satır hatalı</div>
    {r.hatalar.length > 0 && <div className="mt-3 max-h-64 overflow-y-auto erp-scroll text-xs space-y-1" data-testid="aktarim-hatalar">{r.hatalar.map((h) => <div key={h.satir}><span className="font-mono text-muted-foreground mr-2">Satır {h.satir}</span>{h.mesaj}</div>)}</div>}
  </GlassCard>
);

export default function Aktarim() {
  const [tur, setTur] = useState("kolaybi");
  const [alanlar, setAlanlar] = useState(null);
  const [p, setP] = useState(null);
  const [kol, setKol] = useState({});
  const [sonuc, setSonuc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/aktarim/alanlar").then((r) => setAlanlar(r.data)); }, []);
  if (!alanlar) return null;
  const A = alanlar[tur] || [];
  const degis = (t) => { setTur(t); setP(null); setSonuc(null); };
  const yukle = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("dosya", file);
    setBusy(true);
    try { const { data } = await api.post("/aktarim/onizle", fd, { headers: { "Content-Type": "multipart/form-data" } }); setP(data); setKol(tahmin(A, data.kolonlar)); setSonuc(null); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const aktar = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/aktarim/${tur}`, { kolonlar: kol, satirlar: p.satirlar }); setSonuc(data); toast.success(`${data.eklenen} kayıt eklendi`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const ops = [{ value: "-", label: "—" }, ...(p?.kolonlar || []).map((k, i) => ({ value: String(i), label: k }))];
  return (
    <div data-testid="aktarim-page">
      <PageHeader eyebrow="Sistem" title="İçe Aktar" desc="Eski programdan (KolayBi) veya Excel / CSV ile ürün, cari ve stok listelerinizi yükleyin. Önce ürünleri, sonra açılış stoklarını yükleyin." />
      <Tabs value={tur} onValueChange={degis}><TabsList className="bg-white/5 flex-wrap h-auto mb-4">{TURLER.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`aktarim-tab-${k}`}>{l}</TabsTrigger>)}</TabsList></Tabs>
      {tur === "kaliplar" ? <GlassCard className="p-6 space-y-3" data-testid="aktarim-kalip"><div className="font-medium">Kalıp kartları ve ürün bağlantıları</div><p className="text-xs text-muted-foreground">Örnek şablonu indirip doldurun ya da sitenizden indirdiğiniz WooCommerce dosyasını yükleyin. Önce önizleme gösterilir, onaylayınca kalıplar açılır ve ürünlere bağlanır. Düzeltmeler Üretim › Kalıp & Kapasite ekranından yapılır.</p><KalipAktarim /></GlassCard> : tur === "kolaybi" ? <div className="space-y-4"><KolaybiCari /><TopluEkstre /></div> : tur === "woo" ? <WooAktarim /> : tur === "hammadde" ? <HammaddeStok /> : tur === "araclar" ? <AracAktarim /> : <GlassCard className="p-6 space-y-5">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => sablonIndir(tur, A)} data-testid="aktarim-sablon" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border border-white/10 hover:bg-white/10"><Download className="w-4 h-4" />Boş şablon indir</button>
          <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm grad-bg text-white cursor-pointer" data-testid="aktarim-dosya-label">
            <Upload className="w-4 h-4" />{busy && !p ? "Okunuyor…" : p?.dosya_adi || "Dosya seç"}
            <input type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" data-testid="aktarim-dosya" onChange={(e) => { yukle(e.target.files?.[0]); e.target.value = ""; }} /></label>
        </div>
        {tur === "cariler" && <p className="text-xs text-muted-foreground">Açılış bakiyesi: müşteri size borçluysa artı, siz borçluysanız eksi yazın. Tip boşsa müşteri kabul edilir.</p>}
        {tur === "stoklar" && <p className="text-xs text-muted-foreground">Ürün kodu sistemdeki ürünle, depo kodu veya adı Lokasyonlar ekranındaki depoyla aynı olmalı.</p>}
        {p && <>
          <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Sütun eşleştirme · {p.satirlar.length} satır</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{A.map((a) => (
              <Field key={a.kod} label={a.ad}><SelectBox testid={`aktarim-kolon-${a.kod}`} value={kol[a.kod] === null || kol[a.kod] === undefined ? "-" : String(kol[a.kod])} onChange={(v) => setKol((s) => ({ ...s, [a.kod]: v === "-" ? null : Number(v) }))} options={ops} /></Field>
            ))}</div></div>
          <div className="flex justify-end"><GradButton onClick={aktar} disabled={busy} data-testid="aktarim-baslat">{p.satirlar.length} satırı aktar</GradButton></div>
        </>}
      </GlassCard>}
      {sonuc && <Sonuc r={sonuc} />}
    </div>
  );
}
